import React, {useCallback, useState} from 'react'
import {
  Alerts,
  ErrorLoadingRetry,
  FormBuilderFormRenderer,
  RPCs,
  useReportEntry,
  useReportEntryType,
  useRPCContext,
} from '@therms/react-modules'
import {FormLayout, Loading} from '@therms/atalaya'
import * as Yup from 'yup'
import {Formik} from 'formik'

const ValidationSchema = Yup.object().shape({})

interface EntryFormProps {
  entryId: string
  entryTypeId: string
  formBuilderValidation?: {[key: string]: string} | undefined
  onCancel?: () => void
  onComplete: () => void
  onFormBuilderValidate?: (errors?: {[key: string]: string}) => void
  reportId: string
}

function MultiEntryForm({
  entryId,
  entryTypeId,
  formBuilderValidation,
  onCancel,
  onComplete,
  onFormBuilderValidate,
  reportId,
}: EntryFormProps) {
  const [saving, setSaving] = useState(false)

  const {rpcClient} = useRPCContext()

  const {data: entryData, loading: entryLoading} = useReportEntry(
    entryId ? {entryId, reportId} : undefined,
  )
  const {
    data: entryTypeData,
    loading: entryTypeLoading,
    refresh,
  } = useReportEntryType(
    // the `entryData` has the entryType if we're editing an existing entryId
    !entryId && entryTypeId ? {entryTypeId} : undefined,
  )

  const save = useCallback(
    async (values) => {
      setSaving(true)

      try {
        await rpcClient.call(
          entryId
            ? RPCs.Reports.UpdateReportEntry.v2
            : RPCs.Reports.CreateReportEntry.v2,
          {
            entry: values,
            reportId,
          },
        )

        onComplete()

        Alerts.success(`Entry saved`)
      } catch (err: any) {
        setSaving(false)

        Alerts.error('There was a problem saving the Entry', err.message)

        console.log('save() err', err)
      }
    },
    [entryId, onComplete],
  )

  const handleDelete = useCallback(async () => {
    try {
      await rpcClient.call(RPCs.Reports.DeleteReportEntry.v1, {
        entryId,
      })

      onComplete()
    } catch (err: any) {
      console.log('handleDelete() err', err)
    }
  }, [entryId, onComplete])

  if ((entryTypeLoading || entryLoading) && !entryTypeData) {
    return (
      <div className="h-20 w-full text-center">
        <Loading message="Loading Entry" messagePosition="vertical" />
      </div>
    )
  }

  const formId =
    entryData?.entry?.formInput?.formId || entryTypeData?.entryType?.formId

  if (!formId) return <ErrorLoadingRetry onRetry={refresh} />

  return (
    <Formik
      enableReinitialize
      initialValues={entryData?.entry || {entryTypeId, formInput: {fields: {}}}}
      onSubmit={save}
      validationSchema={ValidationSchema}
      // large forms are affected by this CORE-3257
      validateOnChange={false}
    >
      {({setFieldValue, submitForm, values}) => (
        <FormLayout>
          {saving && <Loading message="Saving Entry" overlay />}

          <FormBuilderFormRenderer
            formId={formId}
            formValues={values.formInput.fields}
            initialValue={entryData?.entry?.formInput.fields}
            onFormErrorStateChange={onFormBuilderValidate}
            onFormInputChange={(_values) => {
              setFieldValue('formInput', _values)
            }}
          />

          <FormLayout.Controls
            disabled={!!formBuilderValidation}
            onCancel={onCancel}
            onDelete={entryId ? handleDelete : undefined}
            onSubmit={submitForm}
          />
        </FormLayout>
      )}
    </Formik>
  )
}

export {MultiEntryForm}
